import { chosenOptionIds, type MultiKind } from '@anytracker/core'

import styles from './EnumInput.module.css'
import multi from './MultiInput.module.css'
import type { ValueInputProps } from './types.js'

interface Props extends ValueInputProps {
  readonly kind: MultiKind
}

/**
 * Several of a set, as toggleable badges (T-1301).
 *
 * It borrows `EnumInput`'s badges on purpose: the two controls look at the same
 * option list and differ only in how many answers they take, so making them look
 * different would suggest a difference in the data that is not there. What does
 * differ is the semantics, and those are not cosmetic:
 *
 *   - **`aria-pressed`, not `role="radio"`.** A radio group announces "one of
 *     five" and lets arrow keys move a single selection; a set of toggles
 *     announces each badge's own on/off state, which is what is actually true
 *     here. Reusing the radio group would tell a screen-reader user that
 *     choosing one unchooses the last, which is exactly wrong.
 *   - **No roving tabindex.** Every badge is independently reachable by Tab,
 *     because every badge is an independent decision.
 *
 * Deselecting the last badge records **nothing**, not an empty list. An empty
 * selection is the absence of a measurement, and core refuses to store one.
 */
export function MultiInput({
  kind,
  value,
  onChange,
  disabled = false,
  labelledBy,
  describedBy,
  variant = 'compact',
}: Props) {
  // Ids, never labels — renaming an option must not rewrite history (ADR-0005).
  // Read through core, so this control, the track editor and the guard behind
  // it cannot disagree about which options a value holds.
  const chosen = new Set(value === null ? [] : chosenOptionIds(value))

  /*
   * Archived options are hidden, but a previously chosen one still shows.
   *
   * Otherwise editing an old entry silently drops the answer it was recorded
   * with: the badge is not on screen, so saving writes a value the user never
   * changed and never saw.
   */
  const shown = kind.options.filter(
    (option) => option.archived !== true || chosen.has(option.id),
  )

  function toggle(id: string) {
    const next = kind.options
      .filter((option) => (option.id === id ? !chosen.has(id) : chosen.has(option.id)))
      .map((option) => option.id)
    // Nothing chosen is nothing recorded, so the value goes away rather than
    // becoming `[]` — which core refuses, and rightly.
    onChange(next.length === 0 ? null : next)
  }

  return (
    <div
      className={`${styles['root']} ${variant === 'full' ? styles['full'] : ''}`}
      role="group"
      aria-labelledby={labelledBy}
      aria-describedby={describedBy}
    >
      {shown.map((option) => {
        const selected = chosen.has(option.id)
        return (
          <button
            key={option.id}
            type="button"
            aria-pressed={selected}
            disabled={disabled}
            className={`${styles['option']} ${selected ? styles['selected'] : ''} ${
              option.archived === true ? multi['retired'] : ''
            }`}
            onClick={() => toggle(option.id)}
          >
            {option.colour !== undefined && (
              <span
                className={styles['swatch']}
                style={{ background: option.colour }}
                aria-hidden="true"
              />
            )}
            <span className={styles['label']}>{option.label}</span>
            {selected && (
              <svg
                viewBox="0 0 24 24"
                width={variant === 'full' ? 22 : 16}
                height={variant === 'full' ? 22 : 16}
                fill="none"
                aria-hidden="true"
                className={styles['tick']}
              >
                <path
                  d="M5 13l4 4 10-10"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            )}
          </button>
        )
      })}

      {/*
        How many, quietly. With eight badges on a phone the chosen ones scroll
        out of sight, and "3 chosen" is the only way to know the control is
        holding anything without scrolling back.

        Hidden from assistive technology, not exposed as a live region. Each
        badge already announces its own pressed state as it is toggled, and a
        second reading on every tap is the doubling-up that the scale words
        deliberately avoid. Someone moving through the group hears the state of
        each badge they land on, which is the same information without the
        interruption.
      */}
      {chosen.size > 0 && (
        <p className={multi['count']} aria-hidden="true">
          {/*
            Out of what is on screen, not out of what is still offered. An entry
            holding two options that have since been archived would otherwise
            read "3 chosen of 1".
          */}
          {String(chosen.size)} chosen of {String(shown.length)}
        </p>
      )}
    </div>
  )
}
